"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  Building2,
  FileText,
  Printer,
  Receipt,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import { api } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const features = [
  {
    icon: Receipt,
    title: "GST-ready invoices",
    desc: "Tax lines, GSTIN on bills, and sales history built for Indian retail.",
  },
  {
    icon: ScanLine,
    title: "POS billing",
    desc: "Fast checkout, barcode scan, split payments, and UPI-friendly flows.",
  },
  {
    icon: Printer,
    title: "Thermal receipts",
    desc: "80mm-style print preview so counter staff can hand customers a clean slip.",
  },
  {
    icon: FileText,
    title: "Public invoice links",
    desc: "Share a secure link so buyers can open or print the bill without logging in.",
  },
  {
    icon: Building2,
    title: "Customer ledger",
    desc: "Track dues, credits, and contact details alongside every sale.",
  },
  {
    icon: Truck,
    title: "Purchase & GRN",
    desc: "Purchase orders through goods receipt — stock stays aligned with reality.",
  },
  {
    icon: BarChart3,
    title: "GSTR-friendly reports",
    desc: "Sales and tax exports to support filing workflows your CA expects.",
  },
  {
    icon: ShieldCheck,
    title: "Roles & audit",
    desc: "Branch-aware access, shifts, and audit trail for serious operations.",
  },
];

export default function LandingPage() {
  const [logoFailed, setLogoFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [gstin, setGstin] = useState("");
  const [address, setAddress] = useState("");
  const [message, setMessage] = useState("");
  const [hp, setHp] = useState("");

  async function submitInquiry(e: React.FormEvent) {
    e.preventDefault();
    if (hp.trim() !== "") {
      return;
    }
    const nameOk = companyName.trim().length >= 2;
    const hasContact = phone.trim().length > 0 || email.trim().length > 0;
    if (!nameOk) {
      toast.error("Enter your company or shop name (at least 2 characters).");
      return;
    }
    if (!hasContact) {
      toast.error("Enter at least a phone number or email so we can reach you.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/public/inquiries", {
        company_name: companyName.trim(),
        contact_person: contactPerson.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        gstin: gstin.trim() || null,
        address: address.trim() || null,
        message: message.trim() || null,
      });
      toast.success("Thanks — we received your inquiry. Our team will contact you soon.");
      setCompanyName("");
      setContactPerson("");
      setPhone("");
      setEmail("");
      setGstin("");
      setAddress("");
      setMessage("");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error ?? "Could not send inquiry. Try again later.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div
          className="absolute -left-1/4 top-0 h-[520px] w-[70%] rounded-full bg-gradient-to-br from-violet-500/25 via-fuchsia-500/15 to-transparent blur-3xl"
          aria-hidden
        />
        <div
          className="absolute -right-1/4 top-40 h-[480px] w-[60%] rounded-full bg-gradient-to-bl from-sky-500/20 via-cyan-500/10 to-transparent blur-3xl"
          aria-hidden
        />
      </div>

      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5 font-semibold tracking-tight">
            {!logoFailed ? (
              <img
                src="/app-logo.png"
                alt=""
                className="h-9 w-auto object-contain"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-xs font-semibold leading-tight">
                inv
              </span>
            )}
            <span className="hidden sm:inline">invoice.cprawat.com</span>
          </Link>
          <nav className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <a href="#features">Features</a>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a href="#inquiry">Inquiry</a>
            </Button>
            <Button size="sm" asChild>
              <Link to="/login">Log in</Link>
            </Button>
          </nav>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-6xl px-4 pb-16 pt-12 sm:px-6 sm:pt-16">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-border/80 bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              Invoice, POS & inventory in one place
            </p>
            <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Run the counter and the books without the chaos.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-pretty text-lg text-muted-foreground sm:text-xl">
              invoice.cprawat.com helps shops issue GST-aligned bills, print thermal receipts, manage stock
              from purchase to sale, and keep customers and compliance in sync.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button size="lg" asChild className="rounded-xl px-8">
                <Link to="/login">Log in to app</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="rounded-xl px-8">
                <a href="#inquiry">Request an inquiry</a>
              </Button>
            </div>
          </div>
        </section>

        <section id="features" className="border-y border-border/60 bg-muted/20 py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                Everything around the invoice
              </h2>
              <p className="mt-3 text-muted-foreground">
                From first scan at POS to month-end reports — one stack for front desk and back
                office.
              </p>
            </div>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {features.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className={cn(
                    "rounded-2xl border border-border/80 bg-card/80 p-5 shadow-sm backdrop-blur-sm",
                    "transition hover:border-violet-500/30 hover:shadow-md",
                  )}
                >
                  <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/15 to-fuchsia-500/10 text-violet-600 dark:text-violet-400">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold leading-snug">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="inquiry" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-16 sm:px-6 sm:py-20">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Request an inquiry</h2>
              <p className="mt-3 text-muted-foreground">
                Tell us about your store or chain. We route this to our product team as a lead — you
                will see updates once we connect your workspace.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
                <li>— Demo or onboarding for your team</li>
                <li>— Multi-branch and GST questions</li>
                <li>— Migration from spreadsheets or another POS</li>
              </ul>
            </div>
            <form
              onSubmit={(e) => void submitInquiry(e)}
              className="rounded-2xl border border-border/80 bg-card p-6 shadow-sm"
            >
              <input
                type="text"
                name="website"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden
              />
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="inq-company">Company / shop name *</Label>
                  <Input
                    id="inq-company"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. Shree Kirana Store"
                    required
                    minLength={2}
                  />
                </div>
                <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="inq-contact">Contact person</Label>
                    <Input
                      id="inq-contact"
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="inq-phone">Phone</Label>
                    <Input
                      id="inq-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91…"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="inq-email">Email</Label>
                  <Input
                    id="inq-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@business.com"
                  />
                </div>
                <p className="text-xs text-muted-foreground">* Provide phone or email (or both).</p>
                <div className="grid gap-2">
                  <Label htmlFor="inq-gstin">GSTIN (optional)</Label>
                  <Input id="inq-gstin" value={gstin} onChange={(e) => setGstin(e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="inq-address">Address (optional)</Label>
                  <Input id="inq-address" value={address} onChange={(e) => setAddress(e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="inq-msg">Message</Label>
                  <textarea
                    id="inq-msg"
                    className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Outlets, users, timeline…"
                  />
                </div>
                <Button type="submit" className="w-full sm:w-auto" disabled={busy}>
                  {busy ? "Sending…" : "Submit inquiry"}
                </Button>
              </div>
            </form>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/60 bg-muted/30 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 text-center text-sm text-muted-foreground sm:flex-row sm:text-left sm:px-6">
          <p>© {new Date().getFullYear()} invoice.cprawat.com. All rights reserved.</p>
          <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
            Staff login
          </Link>
        </div>
      </footer>
    </div>
  );
}
